import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Upload, Loader2, Printer, FileText, CheckCircle2, AlertTriangle, Trash2, X,
  Banknote, Search,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

function abrirPDF(loteId: number) {
  const token = localStorage.getItem('pain_token')
  fetch(`/api/financeiro/retornos/${loteId}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob() })
    .then(blob => {
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    })
    .catch(e => toast.error(`Falha no PDF: ${e.message}`))
}

const SIT: Record<string, { txt: string; cls: string }> = {
  baixado: { txt: 'Baixado', cls: 'bg-green-100 text-green-700' },
  baixar: { txt: 'A baixar', cls: 'bg-sky-100 text-sky-700' },
  ja_baixado: { txt: 'Já baixado', cls: 'bg-slate-100 text-slate-600' },
  nao_encontrado: { txt: 'Não encontrado', cls: 'bg-red-100 text-red-700' },
  registro: { txt: 'Registro', cls: 'bg-sky-100 text-sky-700' },
  outro: { txt: 'Info', cls: 'bg-slate-100 text-slate-500' },
}

function dt(s?: string | null) {
  if (!s) return '—'
  try { return new Date(s + (s.length <= 10 ? 'T00:00:00' : '')).toLocaleDateString('pt-BR') } catch { return s }
}

function Card({ label, value, tone = 'slate' }: { label: string; value: any; tone?: string }) {
  const tones: Record<string, string> = {
    slate: 'bg-slate-50 text-slate-700', green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700', amber: 'bg-amber-50 text-amber-700', sky: 'bg-sky-50 text-sky-700',
  }
  return (
    <div className={`rounded-lg px-3 py-2 ${tones[tone]}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-bold leading-tight">{value}</div>
    </div>
  )
}

function LoteModal({ loteId, onClose }: { loteId: number; onClose: () => void }) {
  const qc = useQueryClient()
  const [filtro, setFiltro] = useState('')
  const { data: lote, isLoading } = useQuery<any>({
    queryKey: ['retorno', loteId],
    queryFn: async () => (await api.get(`/financeiro/retornos/${loteId}`)).data,
  })

  const confirmar = useMutation({
    mutationFn: async () => (await api.post(`/financeiro/retornos/${loteId}/confirmar`)).data,
    onSuccess: (d) => {
      toast.success(`Baixa confirmada: ${d.qtd_baixados} título(s) baixado(s)`)
      qc.invalidateQueries({ queryKey: ['retorno', loteId] })
      qc.invalidateQueries({ queryKey: ['retornos'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Falha ao confirmar'),
  })
  const excluir = useMutation({
    mutationFn: async () => (await api.delete(`/financeiro/retornos/${loteId}`)).data,
    onSuccess: () => { toast.success('Prévia excluída'); qc.invalidateQueries({ queryKey: ['retornos'] }); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Falha ao excluir'),
  })

  if (isLoading || !lote) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <Loader2 className="animate-spin text-white" />
      </div>
    )
  }
  const det: any[] = lote.detalhe || []
  const detF = det.filter(d => !filtro ||
    `${d.cliente} ${d.documento} ${d.nosso_numero} ${d.ocorrencia_desc}`.toLowerCase().includes(filtro.toLowerCase()))
  const previa = lote.status === 'previa'
  const problemas = lote.qtd_nao_encontrados

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-3 sm:p-6">
      <div className="w-full max-w-6xl rounded-xl bg-white shadow-xl">
        {/* header */}
        <div className="flex items-center justify-between rounded-t-xl bg-slate-800 px-5 py-3 text-white">
          <div>
            <div className="flex items-center gap-2 text-base font-bold">
              <Banknote size={18} /> Retorno bancário #{lote.id} — {lote.banco_nome || '—'}
            </div>
            <div className="text-xs text-slate-300">
              {lote.arquivo_nome} · {(lote.layout || '').toUpperCase()} · gerado {dt(lote.gerado_em)}
              {' · '}
              <span className={`ml-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${previa ? 'bg-amber-400 text-amber-950' : 'bg-green-400 text-green-950'}`}>
                {previa ? 'PRÉVIA (não baixado)' : 'PROCESSADO'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-white/10"><X size={20} /></button>
        </div>

        {/* cards */}
        <div className="grid grid-cols-2 gap-2 px-5 py-4 sm:grid-cols-4 lg:grid-cols-7">
          <Card label="Ocorrências" value={lote.qtd_ocorrencias} />
          <Card label="Liquidados" value={lote.qtd_liquidados} />
          <Card label={previa ? 'A baixar' : 'Baixados'} value={previa ? lote.qtd_baixar : lote.qtd_baixados} tone="green" />
          <Card label="Já baixados" value={lote.qtd_ja_baixados} />
          <Card label="Não achados" value={lote.qtd_nao_encontrados} tone={problemas ? 'red' : 'slate'} />
          <Card label="Juros" value={formatMoney(lote.valor_juros)} tone="amber" />
          <Card label="Creditado" value={formatMoney(lote.valor_pago)} tone="green" />
        </div>

        {/* ações */}
        <div className="flex flex-wrap items-center gap-2 border-y bg-slate-50 px-5 py-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
            <input value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Filtrar..."
              className="rounded border py-1.5 pl-7 pr-2 text-sm" />
          </div>
          <div className="flex-1" />
          <button onClick={() => abrirPDF(lote.id)}
            className="flex items-center gap-1.5 rounded bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800">
            <Printer size={15} /> Imprimir PDF
          </button>
          {previa && (
            <>
              <button onClick={() => pedirExclusao({ descricao: `esta prévia de retorno (#${lote.id}${lote.banco_nome ? ` — ${lote.banco_nome}` : ''})`, onConfirm: () => excluir.mutate() })} disabled={excluir.isPending}
                className="flex items-center gap-1.5 rounded border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50">
                <Trash2 size={15} /> Excluir prévia
              </button>
              <button onClick={() => { if (confirm(`Confirmar a baixa de ${lote.qtd_baixar} título(s)?`)) confirmar.mutate() }}
                disabled={confirmar.isPending || lote.qtd_baixar === 0}
                className="flex items-center gap-1.5 rounded bg-green-600 px-4 py-1.5 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50">
                {confirmar.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                Confirmar baixa ({lote.qtd_baixar})
              </button>
            </>
          )}
        </div>

        {/* tabela detalhe */}
        <div className="max-h-[55vh] overflow-auto px-2 pb-4">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b text-left text-xs uppercase text-slate-500">
                <th className="px-2 py-2">Documento</th>
                <th className="px-2 py-2">Nosso nº</th>
                <th className="px-2 py-2">Cliente</th>
                <th className="px-2 py-2">Ocorrência</th>
                <th className="px-2 py-2">Venc.</th>
                <th className="px-2 py-2">Liq.</th>
                <th className="px-2 py-2 text-right">Título</th>
                <th className="px-2 py-2 text-right">Pago</th>
                <th className="px-2 py-2 text-right">Juros</th>
                <th className="px-2 py-2">Situação</th>
              </tr>
            </thead>
            <tbody>
              {detF.map((d, i) => {
                const s = SIT[d.situacao] || SIT.outro
                const bg = d.situacao === 'baixado' || d.situacao === 'baixar' ? 'bg-green-50/40'
                  : d.situacao === 'nao_encontrado' ? 'bg-red-50/40' : ''
                return (
                  <tr key={i} className={`border-b border-slate-100 ${bg}`}>
                    <td className="px-2 py-1.5 font-mono text-xs">{d.documento || '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">{d.nosso_numero || '—'}</td>
                    <td className="px-2 py-1.5">{d.cliente || '—'}</td>
                    <td className="px-2 py-1.5 text-xs">{d.ocorrencia}-{d.ocorrencia_desc}</td>
                    <td className="px-2 py-1.5 text-xs">{dt(d.vencimento)}</td>
                    <td className="px-2 py-1.5 text-xs">{dt(d.data_ocorrencia)}</td>
                    <td className="px-2 py-1.5 text-right">{formatMoney(d.valor_titulo)}</td>
                    <td className="px-2 py-1.5 text-right font-medium">{formatMoney(d.valor_pago)}</td>
                    <td className={`px-2 py-1.5 text-right ${d.juros ? 'text-amber-600' : 'text-slate-300'}`}>
                      {d.juros ? formatMoney(d.juros) : '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${s.cls}`}>{s.txt}</span>
                      {d.obs && <div className="text-[10px] text-slate-400">{d.obs}</div>}
                    </td>
                  </tr>
                )
              })}
              {detF.length === 0 && (
                <tr><td colSpan={10} className="py-6 text-center text-slate-400">Nenhuma ocorrência</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default function RetornosBancariosPage() {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [aberto, setAberto] = useState<number | null>(null)

  const { data: lotes = [], isLoading } = useQuery<any[]>({
    queryKey: ['retornos'],
    queryFn: async () => (await api.get('/financeiro/retornos')).data,
  })

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('arquivo', file)
      return (await api.post('/financeiro/retornos/previa', fd)).data
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['retornos'] })
      if (d.ja_processado) toast('Atenção: esse arquivo já foi processado antes', { icon: '⚠️' })
      setAberto(d.id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Falha ao ler arquivo'),
  })

  return (
    <div className="mx-auto max-w-7xl p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <Banknote size={22} /> Retornos Bancários (Baixa de Boletos)
          </h1>
          <p className="text-sm text-slate-500">
            Suba o arquivo de retorno do banco (.RET). Mostro tudo que será baixado, problemas e diferenças
            antes de confirmar. Cada retorno fica salvo com relatório em PDF.
          </p>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={upload.isPending}
          className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 font-semibold text-white hover:bg-sky-700 disabled:opacity-50">
          {upload.isPending ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          Subir retorno
        </button>
        <input ref={fileRef} type="file" className="hidden"
          accept=".ret,.RET,.txt,.sai,.SAI,.RET.SAI,*"
          onChange={e => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = '' }} />
      </div>

      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Gerado</th>
              <th className="px-4 py-3">Banco</th>
              <th className="px-4 py-3">Arquivo</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-center">Baixados</th>
              <th className="px-4 py-3 text-center">Problemas</th>
              <th className="px-4 py-3 text-right">Creditado</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="py-8 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></td></tr>}
            {!isLoading && lotes.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-slate-400">
                <FileText className="mx-auto mb-2" /> Nenhum retorno importado ainda. Clique em "Subir retorno".
              </td></tr>
            )}
            {lotes.map(l => (
              <tr key={l.id} className="cursor-pointer border-t hover:bg-slate-50" onClick={() => setAberto(l.id)}>
                <td className="px-4 py-3">{dt(l.gerado_em)}</td>
                <td className="px-4 py-3 font-medium">{l.banco_nome || '—'}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{l.arquivo_nome}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${l.status === 'previa' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                    {l.status === 'previa' ? 'PRÉVIA' : 'PROCESSADO'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center font-semibold text-green-600">
                  {l.status === 'previa' ? `${l.qtd_baixar} a baixar` : l.qtd_baixados}
                </td>
                <td className="px-4 py-3 text-center">
                  {l.qtd_nao_encontrados > 0
                    ? <span className="inline-flex items-center gap-1 font-semibold text-red-600"><AlertTriangle size={13} />{l.qtd_nao_encontrados}</span>
                    : <span className="text-slate-300">0</span>}
                </td>
                <td className="px-4 py-3 text-right font-semibold">{formatMoney(l.valor_pago)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={e => { e.stopPropagation(); abrirPDF(l.id) }} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="PDF">
                    <Printer size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {aberto !== null && <LoteModal loteId={aberto} onClose={() => setAberto(null)} />}
    </div>
  )
}
